import { UsersFilters } from '../../types/filters';
import { QueryParams, useFileDownloader, useFileGeneration, useJobCreation, useJobPolling, UsePaginatedGetParams } from '../../../../api';
import { USER_MANAGEMENT_BASE_URL } from '../consts';
import { useCallback, useEffect } from 'react';

interface Params {
    filters: UsersFilters;
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
}

const convertFiltersToQueryParams = (filters: UsersFilters): UsePaginatedGetParams['queryParams'] => {
    return Object.entries(filters).reduce<UsePaginatedGetParams['queryParams']>(
        (queryParams: QueryParams = {}, [paramKey, paramValue]) => ({
            ...queryParams,
            ...(paramValue.length && {
                [paramKey]: paramValue.toString()
            })
        }),
        {}
    );
};

export const useDownloadUsersCsv = ({ filters, onSuccess, onError }: Params) => {
    const {
        createJob,
        jobId,
        loading: creating
    } = useJobCreation({
        generateUrl: `${USER_MANAGEMENT_BASE_URL}/generateProtectedUsersFilesJob`,
        queryParams: convertFiltersToQueryParams(filters)
    });

    const {
        job,
        loading: polling,
        error: pollingError,
        errorMessage: pollingErrorMessage
    } = useJobPolling<{ responseIds: string[] }>(
        `${USER_MANAGEMENT_BASE_URL}/getProtectedUsersFilesJobStatus/${jobId}`,
        jobId
    );

    const {
        downloadFiles,
        downloading,
        error: downloadError
    } = useFileDownloader({ downloadUrl: `${USER_MANAGEMENT_BASE_URL}/downloadProtectedUsersFile`, onSuccess });

    useEffect(() => {
        if (job?.status === 'completed' && job.result?.responseIds) {
            downloadFiles(job.result.responseIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.jobId, job?.status]);

    useEffect(() => {
        if (pollingError || downloadError) {
            onError?.(pollingErrorMessage || pollingError || downloadError);
        }
    }, [pollingErrorMessage, pollingError, downloadError, onError]);

    return { loading: creating || polling || downloading, generateAndDownloadUsersFile: createJob };
};

export const useDownloadToken2TemplateCsv = ({ onSuccess, onError }) => {
    const {
        generate,
        loading: generating,
        error: generateError
    } = useFileGeneration({
        generateUrl: `${USER_MANAGEMENT_BASE_URL}/generateTokensCsvFormat`
    });

    const {
        downloadFiles,
        downloading,
        error: downloadError
    } = useFileDownloader({ downloadUrl: `${USER_MANAGEMENT_BASE_URL}/downloadTokensCsvFormat`, onSuccess });

    const handleGenerateAndDownload = useCallback(async () => {
        const responseIds = await generate();

        if (responseIds) {
            await downloadFiles(responseIds);
        }
    }, [generate, downloadFiles]);

    useEffect(() => {
        if (generateError || downloadError) {
            onError?.();
        }
    }, [generateError, downloadError, onError]);

    return {
        loading: generating || downloading,
        generateAndDownloadToken2TemplateFile: handleGenerateAndDownload
    };
};

export const useDownloadLatestUsersUploadReport = ({ onSuccess, onError }) => {
    const { downloadFiles, downloading, error } = useFileDownloader({
        downloadUrl: `${USER_MANAGEMENT_BASE_URL}/downloadLatestUsersUploadReport`,
        onSuccess
    });

    const downloadLatestUsersUploadReport = useCallback(
        async (responseId) => {
            if (responseId) {
                await downloadFiles(responseId);
            }
        },
        [downloadFiles]
    );

    return {
        loading: downloading,
        downloadLatestUsersUploadReport
    };
};

export const useDownloadLatestToken2UploadReport = ({ onSuccess, onError }) => {
    const { downloadFiles, downloading, error } = useFileDownloader({
        downloadUrl: `${USER_MANAGEMENT_BASE_URL}/downloadLatestToken2UploadReport`,
        onSuccess
    });

    const downloadLatestToken2UploadReport = useCallback(
        async (responseId) => {
            if (responseId) {
                await downloadFiles(responseId);
            }
        },
        [downloadFiles]
    );

    return {
        loading: downloading,
        error,
        downloadLatestToken2UploadReport
    };
};
